import { ChangeDetectorRef, Component, inject, OnInit, ViewEncapsulation } from "@angular/core";
import { ReactiveFormsModule, FormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgGlyph } from "@ng-icons/core";
import { PartnerEffects } from "../../State/PartnerEffects";
import { PartnerState } from "../../State/PartnerState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
  TableFilterChangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { Partner } from "../../Dto/PartnerModel";
import { PartnerPublicRequestLink } from "../../Dto/PartnerPublicRequestLink";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { PartnerType } from "../../Dto/PartnerModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { WarehouseApi } from "../../../Warehouse/Api/WarehouseApi";

@Component({
  selector: "app-partner-home",
  standalone: true,
  imports: [ReactiveFormsModule, AppTableComponent, AppButtonComponent, AutocompleteComponent, NgGlyph],
  templateUrl: "./PartnerHomePage.html",
  styleUrl: "./PartnerHomePage.scss",
  encapsulation: ViewEncapsulation.None,
})
export class PartnerHomePage implements OnInit {
  private readonly partnerEffects = inject(PartnerEffects);
  readonly partnerState = inject(PartnerState);
  private readonly router = inject(Router);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly warehouseApi = inject(WarehouseApi);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  selectedTypeFilter = "";
  readonly partnerTypeFilterOptions = Object.values(PartnerType).map((type) => ({ value: type, label: type }));
  publicLinkPartner: Partner | null = null;
  publicLinkMetadata: PartnerPublicRequestLink | null = null;
  publicLinkWarehouseOptions: AutocompleteOption[] = [];
  generatedPublicUrl = "";
  isPublicLinkModalOpen = false;
  isPublicLinkLoading = false;
  isPublicLinkSaving = false;
  publicLinkForm = this.fb.group({
    warehouseId: ["", [Validators.required]],
  });

  partnerColumns: TableColumn<Partner>[] = [
    { key: "id", header: "ID", sortable: true, width: "10%", visibleByDefault: false },
    { key: "name", header: "Name", sortable: true, width: "20%" },
    {
      key: "type",
      header: "Type",
      sortable: true,
      width: "10%",
      cellTemplate: (item) => item.type,
      cellClass: (item) => this.getPartnerTypeChipClass(item.type),
      cellIcon: (item) => this.getPartnerTypeChipIcon(item.type),
    },
    {
      key: "address",
      header: "Address",
      sortable: false,
      width: "40%",
      cellTemplate: (item: Partner) =>
        `${item.address.stateProvinceRegion}, ${item.address.countyDistrict}, ${item.address.subdistrictMunicipality}, ${item.address.communeVillage}, ${item.address.line} ${item.address.zip}`,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "20%",
      cellTemplate: (item) => formatDateNumeric(item.createdAt),
    },
  ];

  get partnerConfig(): TableConfig<Partner> {
    return {
      loading: this.partnerState.loading(),
      emptyMessage: this.partnerState.error(),
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search partners...",
      showCreate: true,
      createButtonLabel: "Create Partner",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      filters: [
        {
          key: "type",
          label: "Type",
          placeholder: "All partner types",
          options: this.partnerTypeFilterOptions,
          defaultValue: this.selectedTypeFilter,
        },
      ],
      onCreate: () => this.createPartner(),
      actionButtons: [
        {
          label: "public link",
          icon: "link",
          handler: (partner: Partner) => this.openPublicRequestLink(partner),
        },
        {
          label: "edit",
          icon: "edit",
          handler: (partner: Partner) => this.editPartner(partner),
        },
        {
          label: "delete",
          icon: "delete",
          handler: (partner: Partner) => this.deletePartner(partner),
        },
      ],
    };
  }

  get partnerPagination(): TablePagination {
    return this.partnerState.pagination();
  }

  ngOnInit(): void {
    const currentSearchParams = this.partnerState.searchParams();
    this.selectedTypeFilter = currentSearchParams.type ?? "";
    this.partnerEffects.searchPartners(currentSearchParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.partnerState.searchParams();
    this.partnerEffects.searchPartners({ ...currentSearchParams, page: event.page, search: event.search });
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.partnerState.searchParams();
    this.partnerEffects.searchPartners({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  onSearchChange(event: TableSearchEvent): void {
    const currentSearchParams = this.partnerState.searchParams();
    this.partnerEffects.searchPartners({
      ...currentSearchParams,
      search: event.term,
      page: event.page,
    });
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.partnerState.searchParams();
    this.partnerEffects.searchPartners({
      ...currentSearchParams,
      page: event.page,
      createdAtFrom: event.dateRange.from,
      createdAtTo: event.dateRange.to,
    });
  }

  onFilterChange(event: TableFilterChangeEvent): void {
    this.selectedTypeFilter = event.filters["type"] ?? "";

    const currentSearchParams = this.partnerState.searchParams();
    const nextType = Object.values(PartnerType).find((type) => type === this.selectedTypeFilter.trim());
    if (!nextType) {
      this.partnerEffects.searchPartners({
        ...currentSearchParams,
        type: undefined,
        page: 1,
      });
      return;
    }

    this.partnerEffects.searchPartners({
      ...currentSearchParams,
      type: nextType,
      page: 1,
    });
  }

  createPartner(): void {
    this.router.navigate(["/partners/create"]);
  }

  editPartner(partner: Partner): void {
    this.router.navigate(["/partners/edit", partner.id]).catch(console.error);
  }

  deletePartner(partner: Partner): void {
    this.confirmModalEffects.open({
      title: "Delete Partner",
      message: `Are you sure you want to delete the partner "${partner.name}"? This action cannot be undone.`,
      confirmText: "Delete Partner",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.partnerEffects.deletePartner(partner.id);
          this.toastEffects.show(`Partner "${partner.name}" has been deleted.`, "success");
        } catch (error: unknown) {
          this.toastEffects.show(getBackendErrorMessage(error, `Failed to delete partner "${partner.name}".`), "error");
        }
      },
    });
  }

  async openPublicRequestLink(partner: Partner): Promise<void> {
    this.publicLinkPartner = partner;
    this.publicLinkMetadata = null;
    this.generatedPublicUrl = "";
    this.publicLinkWarehouseOptions = [];
    this.publicLinkForm.reset({ warehouseId: "" });
    this.isPublicLinkModalOpen = true;
    this.isPublicLinkLoading = true;

    try {
      const metadata = await this.partnerEffects.getPublicRequestLink(partner.id);
      setTimeout(() => {
        this.publicLinkMetadata = metadata;
        if (metadata.warehouse) {
          this.publicLinkWarehouseOptions = [{ value: metadata.warehouse.id, label: metadata.warehouse.name }];
          this.publicLinkForm.patchValue({ warehouseId: metadata.warehouse.id });
        }
        this.isPublicLinkLoading = false;
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to load public request link."), "error");
      this.closePublicRequestLink();
      this.isPublicLinkLoading = false;
      this.deferDetectChanges();
    }
  }

  closePublicRequestLink(): void {
    this.isPublicLinkModalOpen = false;
    this.publicLinkPartner = null;
    this.publicLinkMetadata = null;
    this.generatedPublicUrl = "";
    this.publicLinkWarehouseOptions = [];
    this.publicLinkForm.reset({ warehouseId: "" });
  }

  async searchPublicLinkWarehouses(term: string): Promise<void> {
    const response = await this.warehouseApi.searchWarehouses({ search: term, page: 1, limit: 50 });
    const options = response.data.map((warehouse) => ({ value: warehouse.id, label: warehouse.name }));
    this.publicLinkWarehouseOptions = this.mergeUniqueOptions(this.publicLinkWarehouseOptions, options);
  }

  onPublicLinkWarehouseChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.publicLinkForm.patchValue({ warehouseId: value.toString() });
  }

  async regeneratePublicRequestLink(): Promise<void> {
    if (!this.publicLinkPartner) {
      return;
    }

    if (this.publicLinkForm.invalid) {
      this.publicLinkForm.markAllAsTouched();
      this.toastEffects.show("Choose a default warehouse before generating the link.", "error");
      return;
    }

    this.isPublicLinkSaving = true;
    this.cdr.detectChanges();
    try {
      const warehouseId = this.publicLinkForm.value.warehouseId ?? "";
      const result = await this.partnerEffects.generatePublicRequestLink(this.publicLinkPartner.id, warehouseId);
      const nextPublicUrl = `${window.location.origin}/public/request/${result.token}`;
      const copied = await this.copyPublicUrlToClipboard(nextPublicUrl);

      setTimeout(() => {
        this.publicLinkMetadata = result;
        this.generatedPublicUrl = nextPublicUrl;
        if (result.warehouse) {
          this.publicLinkWarehouseOptions = this.mergeUniqueOptions(this.publicLinkWarehouseOptions, [
            { value: result.warehouse.id, label: result.warehouse.name },
          ]);
        }
        this.isPublicLinkSaving = false;
        this.toastEffects.show(
          (() => {
            if (copied) {
              return "Public request link regenerated and copied.";
            }
            return "Public request link regenerated.";
          })(),
          "success",
        );
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to regenerate public request link."), "error");
      this.isPublicLinkSaving = false;
      this.deferDetectChanges();
    }
  }

  async copyGeneratedPublicUrl(showToast = true): Promise<boolean> {
    if (!this.generatedPublicUrl) {
      this.toastEffects.show("Regenerate the link before copying.", "error");
      return false;
    }

    const copied = await this.copyPublicUrlToClipboard(this.generatedPublicUrl);
    if (copied) {
      if (showToast) {
        this.toastEffects.show("Public request link copied.", "success");
      }
      return true;
    }

    if (showToast) {
      this.toastEffects.show("Copy failed. Select the generated URL manually.", "error");
    }
    return false;
  }

  getPublicLinkStatusLabel(): string {
    if (this.publicLinkMetadata?.expired) {
      return "Expired";
    }
    if (this.publicLinkMetadata?.enabled) {
      return "Active";
    }
    return "Disabled";
  }

  getPublicLinkStatusDescription(): string {
    if (this.publicLinkMetadata?.expired) {
      return "This URL has expired automatically. Regenerate a new URL before sharing the form again.";
    }
    if (this.publicLinkMetadata?.enabled) {
      return "The latest generated URL can create draft requests until it expires.";
    }
    return "Generate a new URL before sharing this public form.";
  }

  getPublicLinkExpiryLabel(): string {
    const expiresAt = this.publicLinkMetadata?.expiresAt;
    if (!expiresAt) {
      return "Each generated URL expires 12 hours after it is created.";
    }
    return `Expires ${formatDateNumeric(expiresAt)}`;
  }

  private async copyPublicUrlToClipboard(publicUrl: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(publicUrl);
      return true;
    } catch {
      return false;
    }
  }

  disablePublicRequestLink(): void {
    if (!this.publicLinkPartner) {
      return;
    }

    this.confirmModalEffects.open({
      title: "Disable public request link?",
      message: `This will immediately invalidate the current public request link for ${this.publicLinkPartner.name}.`,
      confirmText: "Disable link",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        if (!this.publicLinkPartner) {
          return;
        }
        try {
          const metadata = await this.partnerEffects.disablePublicRequestLink(this.publicLinkPartner.id);
          setTimeout(() => {
            this.publicLinkMetadata = metadata;
            this.generatedPublicUrl = "";
            this.toastEffects.show("Public request link disabled.", "success");
            this.cdr.detectChanges();
          }, 0);
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Unable to disable public request link."), "error");
        }
      },
    });
  }

  private mergeUniqueOptions(current: AutocompleteOption[], next: AutocompleteOption[]): AutocompleteOption[] {
    const seen = new Set<string>();
    return [...current, ...next].filter((option) => {
      const key = option.value.toString();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private getPartnerTypeChipClass(type: string): string {
    if (type === PartnerType.School || type === PartnerType.College) {
      return "app-table__chip app-table__chip--school";
    }
    if (type === PartnerType.Church) {
      return "app-table__chip app-table__chip--church";
    }
    if (type === PartnerType.Foundation || type === PartnerType.Ministry) {
      return "app-table__chip app-table__chip--foundation";
    }
    if (type === PartnerType.Government) {
      return "app-table__chip app-table__chip--government";
    }
    if (type === PartnerType.Other) {
      return "app-table__chip app-table__chip--other";
    }
    return "app-table__chip";
  }

  private getPartnerTypeChipIcon(type: string): string {
    if (type === PartnerType.School || type === PartnerType.College) {
      return "school";
    }
    if (type === PartnerType.Church) {
      return "church";
    }
    if (type === PartnerType.Foundation) {
      return "volunteer_activism";
    }
    if (type === PartnerType.Ministry) {
      return "volunteer_activism";
    }
    if (type === PartnerType.Government) {
      return "account_balance";
    }
    return "category";
  }

  private deferDetectChanges(): void {
    setTimeout(() => this.cdr.detectChanges(), 0);
  }
}
